import React, { useId, useMemo } from 'react';
import BlueXLoader from './BlueXLoader';

const BREAKDOWN_ITEMS = [
  { key: 'scale', label: 'Scale', accentClass: 'partner-dashboard__fill--scale', max: 25 },
  { key: 'revenue', label: 'Revenue', accentClass: 'partner-dashboard__fill--revenue', max: 25 },
  { key: 'security_impact', label: 'Security impact', accentClass: 'partner-dashboard__fill--security', max: 25 },
  { key: 'engagement', label: 'Engagement', accentClass: 'partner-dashboard__fill--engagement', max: 15 },
  { key: 'retention', label: 'Retention', accentClass: 'partner-dashboard__fill--retention', max: 10 },
];

const TIER_STEPS = [
  { key: 'bronze', name: 'Bronze', range: '0–49 pts', rate: 10 },
  { key: 'silver', name: 'Silver', range: '50–79 pts', rate: 20 },
  { key: 'gold', name: 'Gold', range: '80–100 pts', rate: 30 },
];

function clampPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value) {
  return `${Math.round(clampPercent(value))}%`;
}

function formatPoints(points) {
  const n = Number(points);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
  return n.toFixed(1);
}

function getCommissionRateByTier(tier) {
  const normalizedTier = String(tier || '').trim().toLowerCase();
  if (normalizedTier === 'gold') return 30;
  if (normalizedTier === 'silver') return 20;
  return 10;
}

function tierPillModifier(tierName) {
  const t = String(tierName || '').trim().toLowerCase();
  if (t === 'gold') return 'gold';
  if (t === 'silver') return 'silver';
  return 'bronze';
}

function isActiveTierStep(currentTier, stepName) {
  return String(currentTier || '').trim().toLowerCase() === String(stepName || '').trim().toLowerCase();
}

function tierStepRank(stepName) {
  const n = String(stepName || '').trim().toLowerCase();
  if (n === 'gold') return 3;
  if (n === 'silver') return 2;
  return 1;
}

function isFutureTierLocked(currentTierName, stepName) {
  return tierStepRank(stepName) > tierStepRank(currentTierName);
}

function engagementFeaturedFootnote(row) {
  if (!row) return '';
  const remaining = Math.max(0, row.max - row.points);
  if (row.points > 0 && row.points < row.max) {
    return `Only active category — ${formatPoints(remaining)} pts still available`;
  }
  if (row.points >= row.max) {
    return 'Engagement category complete for this period.';
  }
  return 'Complete referrals to unlock engagement points.';
}

function breakdownGridFootnote(row) {
  if (!row) return '';
  if (row.points >= row.max) {
    return 'Category complete for this period.';
  }
  if (row.points <= 0) {
    return 'Refer more clients to unlock.';
  }
  return `${formatPoints(row.max - row.points)} pts still available in this category.`;
}

/** UI-balanced shield: subtle crest, broader shoulders, shorter taper (120x120 viewBox) */
const TIER_SHIELD_PATH =
  'M 18 24 Q 40 31 60 18 Q 80 31 102 24 Q 105 50 94 72 Q 82 93 60 108 Q 38 93 26 72 Q 15 50 18 24 Z';
const SHIELD_FILL_BOTTOM = 108;
const SHIELD_FILL_HEIGHT_MAX = 90;
const SHIELD_CLIP_X = 16;
const SHIELD_CLIP_W = 88;

function formatJoinedDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function PartnerDashboard({
  data = null,
  clients = [],
  loading = false,
  error = '',
  onRetry = null,
  themeLight = true,
}) {
  const scoreValue = Math.round(clampPercent(data?.score));
  const tierName = String(data?.tier || 'Bronze');
  const totalClients = Number(data?.total_clients ?? 0);
  const mrrInfluenced = Number(data?.mrr_influenced ?? 0);
  const commissionEarned = Number(data?.commission_earned ?? 0);
  const commissionPending = data?.commission_pending == null ? null : Number(data.commission_pending);
  const commissionRate = Number(data?.commission_rate) > 0
    ? Number(data.commission_rate)
    : getCommissionRateByTier(tierName);
  const referralCode = String(data?.referral_code || '').trim();
  const clientRows = Array.isArray(clients) ? clients : [];
  const isEmpty = !loading && !error && totalClients <= 0;

  const nextTier = data?.next_tier ? String(data.next_tier) : null;
  const nextTierProgress = clampPercent(data?.next_tier_progress);
  const pointsRaw = Number(data?.points_to_next_tier ?? 0);
  const pointsToNextTier = Number.isFinite(pointsRaw) ? Math.max(0, pointsRaw) : 0;
  const ringProgressPct = clampPercent(nextTier ? nextTierProgress : scoreValue);
  const shieldClipHeight = (ringProgressPct / 100) * SHIELD_FILL_HEIGHT_MAX;
  const shieldClipY = SHIELD_FILL_BOTTOM - shieldClipHeight;
  const tierShieldClipId = `partner-tier-shield-clip-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const breakdownRows = useMemo(() => {
    const breakdown = data?.breakdown || {};
    return BREAKDOWN_ITEMS.map((item) => {
      const points = Number(breakdown[item.key] ?? 0);
      const normalizedScore = clampPercent((points / item.max) * 100);
      return {
        ...item,
        points,
        normalizedScore,
        percentLabel: formatPercent(normalizedScore),
      };
    });
  }, [data]);

  const engagementRow = useMemo(
    () => breakdownRows.find((r) => r.key === 'engagement') || null,
    [breakdownRows]
  );

  const breakdownGridRows = useMemo(() => {
    const order = ['scale', 'revenue', 'security_impact', 'retention'];
    return order.map((k) => breakdownRows.find((r) => r.key === k)).filter(Boolean);
  }, [breakdownRows]);

  const themeClass = themeLight ? 'partner-dashboard--light' : 'partner-dashboard--dark';
  const tierThemeClass = `partner-dashboard--tier-${tierPillModifier(tierName)}`;

  return (
    <section
      id="partner-score-panel"
      className={`partner-score-panel partner-dashboard ${themeClass} ${tierThemeClass} assets-section`}
      aria-labelledby="partner-dashboard-title"
    >
      <div className="partner-dashboard__shell">
        <header className="partner-dashboard__header">
          <div className="partner-dashboard__header-text">
            <h2 className="partner-dashboard__title" id="partner-dashboard-title">
              Partner Performance
            </h2>
          </div>
          <div className={`partner-dashboard__tier-pill partner-dashboard__tier-pill--${tierPillModifier(tierName)}`}>
            <span className="partner-dashboard__tier-pill-dot" aria-hidden="true" />
            <span>
              {tierName} · {commissionRate}% commission
            </span>
          </div>
        </header>

        {loading ? (
          <div className="partner-dashboard__state partner-dashboard__state--loading" role="status" aria-live="polite">
            <BlueXLoader label="Loading partner dashboard" />
          </div>
        ) : error ? (
          <div className="partner-dashboard__state partner-dashboard__state--error" role="alert">
            <div className="partner-dashboard__state-title">{error}</div>
            <div className="partner-dashboard__state-subtitle">If this issue persists, contact your administrator.</div>
            {typeof onRetry === 'function' ? (
              <button
                type="button"
                className="partner-dashboard__retry"
                onClick={onRetry}
                aria-label="Retry loading partner dashboard"
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {isEmpty ? (
              <div className="partner-dashboard__state partner-dashboard__state--notice" role="status">
                <div className="partner-dashboard__state-title">No referred clients yet</div>
                <div className="partner-dashboard__state-subtitle">
                  Once your first referral activates, this dashboard will show live score, revenue influence, and commissions.
                </div>
              </div>
            ) : null}

            <div className="partner-dashboard__layout">
              <div className="partner-dashboard__stats">
                <div className="partner-dashboard__stat-card">
                  <div className="partner-dashboard__stat-label">Total clients</div>
                  <div className="partner-dashboard__stat-value">{totalClients}</div>
                  <div className="partner-dashboard__stat-hint">referred</div>
                </div>
                <div className="partner-dashboard__stat-card">
                  <div className="partner-dashboard__stat-label">Revenue influenced</div>
                  <div className="partner-dashboard__stat-value">{formatCurrency(mrrInfluenced)}</div>
                  <div className="partner-dashboard__stat-hint">this period</div>
                </div>
                <div className="partner-dashboard__stat-card">
                  <div className="partner-dashboard__stat-label">Commission earned</div>
                  <div className="partner-dashboard__stat-value">{formatCurrency(commissionEarned)}</div>
                  <div className="partner-dashboard__stat-hint">
                    {commissionPending == null
                      ? 'pending payout'
                      : commissionPending > 0
                        ? `${formatCurrency(commissionPending)} awaiting payout`
                        : 'all paid out'}
                  </div>
                </div>
              </div>

              <div className="partner-dashboard__main">
                <div className="partner-dashboard__panel partner-dashboard__panel--tier">
                <h3 className="partner-dashboard__panel-title">Tier progress</h3>
                <div
                  className="partner-dashboard__tier-ring-wrap"
                  role="img"
                  aria-label={
                    nextTier
                      ? `Progress toward ${nextTier}: ${formatPercent(
                          nextTierProgress
                        )}. Partner score ${scoreValue} of 100. +${formatPoints(
                          pointsToNextTier
                        )} to unlock ${nextTier}.`
                      : `Partner score ${scoreValue} of 100. Top tier.`
                  }
                >
                  <div className="partner-dashboard__tier-ring-chart">
                    <svg
                      className="partner-dashboard__tier-ring-svg partner-dashboard__tier-shield-svg"
                      viewBox="0 0 120 120"
                      aria-hidden="true"
                    >
                      <defs>
                        <clipPath id={tierShieldClipId}>
                          <rect
                            x={SHIELD_CLIP_X}
                            y={shieldClipY}
                            width={SHIELD_CLIP_W}
                            height={Math.max(0, shieldClipHeight)}
                          />
                        </clipPath>
                      </defs>
                      <path className="partner-dashboard__tier-shield-back" d={TIER_SHIELD_PATH} />
                      <path
                        className="partner-dashboard__tier-shield-fill"
                        d={TIER_SHIELD_PATH}
                        clipPath={`url(#${tierShieldClipId})`}
                      />
                    </svg>
                    <div className="partner-dashboard__tier-score-block">
                      <div className="partner-dashboard__tier-score-value" data-testid="partner-dashboard-summary-score">
                        {scoreValue}
                      </div>
                    </div>
                  </div>
                  <p
                    className={`partner-dashboard__tier-next-foot partner-dashboard__tier-next-foot--${tierPillModifier(
                      tierName
                    )}`}
                  >
                    {nextTier
                      ? `+${formatPoints(pointsToNextTier)} to unlock ${nextTier}`
                      : 'You are at the top tier. Tier thresholds: Bronze (0–49), Silver (50–79), Gold (80–100).'}
                  </p>
                </div>
                <ul className="partner-dashboard__tier-list" aria-label="Partner tiers">
                  {TIER_STEPS.map((step) => {
                    const active = isActiveTierStep(tierName, step.name);
                    const locked = isFutureTierLocked(tierName, step.name);
                    return (
                      <li
                        key={step.key}
                        className={`partner-dashboard__tier-row${active ? ' partner-dashboard__tier-row--active' : ''}${
                          locked ? ' partner-dashboard__tier-row--locked' : ''
                        }`}
                        title={
                          locked ? 'Increase your partner score to unlock this tier.' : undefined
                        }
                      >
                        <span className="partner-dashboard__tier-name-group">
                          <span className="partner-dashboard__tier-name">{step.name}</span>
                          <span className="partner-dashboard__tier-commission">{step.rate}%</span>
                        </span>
                        <span className="partner-dashboard__tier-range-wrap">
                          <span className="partner-dashboard__tier-range">{step.range}</span>
                          {locked ? (
                            <span className="partner-dashboard__tier-lock" role="img" aria-label="Locked">
                              <svg
                                className="partner-dashboard__tier-lock-svg"
                                viewBox="0 0 16 16"
                                width="14"
                                height="14"
                                focusable="false"
                                aria-hidden="true"
                              >
                                <path
                                  fill="currentColor"
                                  d="M12 7V5a4 4 0 1 0-8 0v2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zm-6-2a2 2 0 1 1 4 0v2H6V5z"
                                />
                              </svg>
                            </span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="partner-dashboard__panel partner-dashboard__panel--breakdown">
                <h3 className="partner-dashboard__panel-title" id="partner-dashboard-breakdown-title">
                  Category breakdown
                </h3>
                <div className="partner-dashboard__breakdown-layout" aria-labelledby="partner-dashboard-breakdown-title">
                  {engagementRow ? (
                    <div className="partner-dashboard__breakdown-featured">
                      <div className="partner-dashboard__breakdown-featured-top">
                        <div className="partner-dashboard__breakdown-featured-left">
                          <span className="partner-dashboard__breakdown-featured-title-wrap">
                            <span className="partner-dashboard__breakdown-featured-label">
                              {engagementRow.label}
                            </span>
                            {engagementRow.normalizedScore > 0 ? (
                              <span className="partner-dashboard__breakdown-badge partner-dashboard__breakdown-badge--featured">
                                {engagementRow.percentLabel}
                              </span>
                            ) : null}
                          </span>
                        </div>
                        <div className="partner-dashboard__breakdown-featured-meta">
                          <span className="partner-dashboard__breakdown-points partner-dashboard__breakdown-points--featured">
                            {formatPoints(engagementRow.points)} / {engagementRow.max} pts
                          </span>
                        </div>
                      </div>
                      <div
                        className="partner-dashboard__track partner-dashboard__track--featured"
                        aria-hidden="true"
                      >
                        <span
                          className={`partner-dashboard__fill ${engagementRow.accentClass}`}
                          style={{ width: `${engagementRow.normalizedScore}%` }}
                        />
                      </div>
                      <p className="partner-dashboard__breakdown-featured-foot">
                        {engagementFeaturedFootnote(engagementRow)}
                      </p>
                    </div>
                  ) : null}

                  <div className="partner-dashboard__breakdown-grid">
                    {breakdownGridRows.map((row) => (
                      <div className="partner-dashboard__breakdown-grid-cell" key={row.key}>
                        <div className="partner-dashboard__breakdown-row-head partner-dashboard__breakdown-grid-head">
                          <span className="partner-dashboard__breakdown-label">{row.label}</span>
                          <span className="partner-dashboard__breakdown-points">
                            {formatPoints(row.points)} / {row.max} pts
                          </span>
                        </div>
                        <div className="partner-dashboard__track partner-dashboard__track--grid" aria-hidden="true">
                          <span
                            className={`partner-dashboard__fill ${row.accentClass}`}
                            style={{ width: `${row.normalizedScore}%` }}
                          />
                        </div>
                        <p className="partner-dashboard__breakdown-grid-foot">{breakdownGridFootnote(row)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            </div>

            <div className="partner-dashboard__panel partner-dashboard__panel--clients">
              <div className="partner-dashboard__clients-head">
                <h3 className="partner-dashboard__panel-title" id="partner-dashboard-clients-title">
                  Your clients
                </h3>
                {referralCode ? (
                  <span className="partner-dashboard__referral" data-testid="partner-dashboard-referral-code">
                    Referral code <code>{referralCode}</code>
                  </span>
                ) : null}
              </div>
              {clientRows.length ? (
                <ul className="partner-dashboard__clients" aria-labelledby="partner-dashboard-clients-title">
                  {clientRows.map((client) => (
                    <li className="partner-dashboard__client" key={client.uu_id || client.id}>
                      <span className="partner-dashboard__client-name">{client.name}</span>
                      {formatJoinedDate(client.created_at) ? (
                        <span className="partner-dashboard__client-date">
                          since {formatJoinedDate(client.created_at)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="partner-dashboard__stat-hint">
                  Clients appear here once they sign up with your referral code or are assigned to you.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
